import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Badge, Box, Tooltip, createStyles, makeStyles } from '@material-ui/core';
import { Menu, IconButton } from 'new-design/common/ui';
import clsx from 'clsx';
import { ChatIcon, OneSiteIcon, CameraIcon, GroupIcon } from 'assets/icons';
import * as chat from 'ducks/chat';
import * as auth from 'ducks/auth';
import { getUserInfo } from 'ducks/auth';
import { BASE_APP_PATH } from 'new-design/routes/common/constants';
import { CHANNEL_ICONS, isBranch, getLastMessagePreview } from 'new-design/containers/operations/Chat/constants';
import { formatChannelListTime } from 'new-design/containers/operations/Chat/helpers';
import IntlMessages, { useIntl } from 'util/IntlMessages';

const useStyles = makeStyles(
  (theme) =>
    createStyles({
      iconRoot: {
        padding: 0,
        margin: -3,
        width: 24,
        height: 24,
        [theme.breakpoints.up('lg')]: {
          width: 20,
          height: 20,
        },
        '& svg path': {
          fill: ({ hasUnread }) => hasUnread ? theme.palette.primary.main : theme.palette.secondary.main,
        },
      },
      badge: {
        '& .MuiBadge-badge': {
          backgroundColor: theme.palette.error.main,
          color: theme.palette.common.white,
          fontWeight: 700,
          fontSize: 9,
          minWidth: 15,
          height: 15,
          padding: '0 3px',
          top: -2,
          right: -4,
        },
      },
      popup: {
        width: 380,
        maxHeight: 520,
        overflow: 'auto',
        borderRadius: 12,
      },
      popupTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: theme.palette.text.primary,
        padding: theme.spacing(2.5, 3, 1.5),
      },
      empty: {
        padding: theme.spacing(3),
        textAlign: 'center',
        fontSize: 13,
        color: theme.palette.text.secondary,
      },
      channelItem: {
        padding: theme.spacing(1, 2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
      channelIcon: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& svg': {
          width: 20,
          height: 20,
        },
      },
      channelIconActive: {
        backgroundColor: theme.palette.green.bg,
        '& svg, & path, & rect': {
          fill: theme.palette.green.color,
        },
      },
      channelIconInactive: {
        backgroundColor: theme.palette.grey.bg,
        '& svg, & path, & rect': {
          fill: theme.palette.grey.color,
        },
      },
      channelInfo: {
        flex: 1,
        minWidth: 0,
      },
      channelTopRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        minWidth: 0,
      },
      channelName: {
        fontSize: 16,
        fontWeight: 500,
        color: theme.palette.text.primary,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
      },
      channelDot: {
        flexShrink: 0,
        color: theme.palette.text.secondary,
        fontSize: 14,
        lineHeight: 1,
      },
      employeeCounter: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
        color: theme.palette.text.secondary,
        fontSize: 12,
        '& svg': {
          width: 14,
          height: 14,
        },
        '& svg, & path, & rect': {
          fill: theme.palette.text.secondary,
        },
      },
      employeeCounterActive: {
        color: theme.palette.green.color,
        '& svg, & path, & rect': {
          fill: theme.palette.green.color,
        },
      },
      channelSubtitle: {
        fontSize: 14,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      previewItalic: {
        fontStyle: 'italic',
      },
      previewSender: {
        color: theme.palette.secondary.main,
      },
      previewPhotoIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        marginRight: 8,
        transform: 'translateY(-1px)',
        '& svg': {
          width: 14,
          height: 14,
        },
        '& svg, & path, & rect': {
          fill: theme.palette.text.secondary,
        },
      },
      previewActivityIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        marginRight: 8,
        transform: 'translateY(-2px)',
        '& svg': {
          width: 14,
          height: 14,
        },
        '& svg, & path, & rect': {
          fill: theme.palette.text.secondary,
        },
      },
      channelMeta: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        alignSelf: 'flex-start',
        gap: theme.spacing(0.5),
        flexShrink: 0,
        marginTop: 2,
      },
      channelTime: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
      },
      channelTimeUnread: {
        color: theme.palette.primary.main,
        fontWeight: 400,
      },
      unreadBadge: {
        flexShrink: 0,
        minWidth: 18,
        height: 18,
        padding: theme.spacing(0, 0.5),
        borderRadius: 9,
        backgroundColor: theme.palette.error.main,
        color: theme.palette.common.white,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      divider: {
        height: 1,
        backgroundColor: '#F0F0F0',
        marginLeft: theme.spacing(3),
        marginRight: theme.spacing(3),
      },
      seeAll: {
        padding: theme.spacing(1.5, 3),
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.primary.main,
        cursor: 'pointer',
        textAlign: 'center',
        borderTop: '1px solid #F0F0F0',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    }),
  {
    name: 'ChatNotifications',
  }
);

const MAX_VISIBLE = 20;

const ChatNotifications = () => {
  const { formatMessage, locale } = useIntl();
  const history = useHistory();
  const hasChatAccess = useSelector(auth.hasChatAccess);
  const totalUnread = useSelector(chat.selectTotalUnreadCount);
  const channelsWithUnread = useSelector(chat.selectChannelsWithUnread);
  const currentUserID = useSelector((state) => getUserInfo(state)?.userID);
  const classes = useStyles({ hasUnread: totalUnread > 0 });
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleChannelClick = useCallback((channelID) => {
    handleClose();
    history.push(`${BASE_APP_PATH}/chat?channelID=${channelID}`);
  }, [history, handleClose]);

  const handleSeeAll = useCallback(() => {
    handleClose();
    history.push(`${BASE_APP_PATH}/chat?showUnreadOnly=1`);
  }, [history, handleClose]);

  const visibleChannels = useMemo(
    () => channelsWithUnread.slice(0, MAX_VISIBLE),
    [channelsWithUnread]
  );
  const hasMore = channelsWithUnread.length > visibleChannels.length;

  if (!hasChatAccess) return null;

  return (
    <>
      <Badge
        badgeContent={totalUnread > 99 ? '99+' : totalUnread}
        invisible={totalUnread === 0}
        overlap="circular"
        className={classes.badge}
        onClick={handleOpen}
      >
        <IconButton className={classes.iconRoot}>
          <Tooltip title={<IntlMessages id="chat" />}>
            <ChatIcon />
          </Tooltip>
        </IconButton>
      </Badge>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box className={classes.popup}>
          <Box className={classes.popupTitle}>
            {formatMessage({ id: 'chat' })} ({channelsWithUnread.length})
          </Box>
          {channelsWithUnread.length === 0 ? (
            <Box className={classes.empty}>
              {formatMessage({ id: 'no_unread_messages' })}
            </Box>
          ) : (
            visibleChannels.map((channel, index) => {
              const Icon = CHANNEL_ICONS[channel.type] || OneSiteIcon;
              const preview = getLastMessagePreview(channel, formatMessage, currentUserID);
              const isActive = channel.employeeCount > 0;
              const showEmployeeCounter =
                !isBranch(channel) && typeof channel.employeeCount === 'number';
              const timeLabel = channel.lastMessage?.dateCreated
                ? formatChannelListTime(channel.lastMessage.dateCreated, formatMessage, locale)
                : '';
              const hasUnread = channel.unreadCount > 0;
              return (
                <React.Fragment key={channel.channelID}>
                  {index > 0 && <Box className={classes.divider} />}
                  <Box
                    className={classes.channelItem}
                    onClick={() => handleChannelClick(channel.channelID)}
                  >
                    <Box
                      className={clsx(classes.channelIcon, {
                        [classes.channelIconActive]: isActive && !isBranch(channel),
                        [classes.channelIconInactive]: !isActive || isBranch(channel),
                      })}
                    >
                      <Icon />
                    </Box>
                    <Box className={classes.channelInfo}>
                      <Box className={classes.channelTopRow}>
                        <Box className={classes.channelName}>{channel.name}</Box>
                        {showEmployeeCounter && (
                          <>
                            <Box className={classes.channelDot}>·</Box>
                            <Box
                              className={clsx(classes.employeeCounter, {
                                [classes.employeeCounterActive]: channel.employeeCount > 0,
                              })}
                            >
                              <GroupIcon />
                              <span>{channel.employeeCount}</span>
                            </Box>
                          </>
                        )}
                      </Box>
                      {preview && (
                        <Box className={classes.channelSubtitle}>
                          {preview.sender && (
                            <span className={classes.previewSender}>{preview.sender}: </span>
                          )}
                          {preview.hasPhoto && (
                            <span className={classes.previewPhotoIcon}>
                              <CameraIcon />
                            </span>
                          )}
                          {preview.activityIcon && (
                            <span className={classes.previewActivityIcon}>
                              <preview.activityIcon />
                            </span>
                          )}
                          <span className={clsx({ [classes.previewItalic]: preview.italic })}>{preview.text}</span>
                        </Box>
                      )}
                    </Box>
                    {(timeLabel || hasUnread) && (
                      <Box className={classes.channelMeta}>
                        {timeLabel && (
                          <Box
                            className={clsx(classes.channelTime, {
                              [classes.channelTimeUnread]: hasUnread,
                            })}
                          >
                            {timeLabel}
                          </Box>
                        )}
                        {hasUnread && (
                          <Box className={classes.unreadBadge}>
                            {channel.unreadCount > 99 ? '99+' : channel.unreadCount}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </React.Fragment>
              );
            })
          )}
          {hasMore && (
            <Box className={classes.seeAll} onClick={handleSeeAll}>
              {formatMessage({ id: 'see_all_unread' })}
            </Box>
          )}
        </Box>
      </Menu>
    </>
  );
};

export default ChatNotifications;
